import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import type { OutreachRecipientDetail } from "../lib/types";
import { StatusBadge } from "./OutreachApprovalPage";
import { skipReasonLabel } from "../lib/skip-reasons";

/** Single outreach recipient — full event timeline + funnel answers + matched review. */

const EVENT_LABEL: Record<string, string> = {
  created: "Pulled from warehouse",
  approved: "Approved for send",
  skipped: "Skipped",
  skipped_eligibility: "Skipped by eligibility check",
  sms_sent: "Text sent",
  sms_failed: "Text failed",
  email_sent: "Email sent",
  email_failed: "Email failed",
  suppressed_optout: "Suppressed — opted out",
  suppressed_quiet_hours: "Held — quiet hours",
  suppressed_no_contact: "No usable contact / consent",
  link_clicked: "Opened the feedback page",
  responded: "Answered the survey",
  private_feedback_submitted: "Sent private feedback to the manager",
  feedback_acknowledged: "Feedback acknowledged",
  review_clicked: "Clicked through to leave a review",
  review_matched: "Matched to a public review",
  opted_out: "Opted out",
};

export function OutreachRecipientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<OutreachRecipientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet<OutreachRecipientDetail>(`/api/outreach/recipients/${id}`);
      setDetail(res);
    } catch (err) {
      console.error("recipient load failed:", err);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const acknowledge = async () => {
    if (!detail || busy) return;
    setBusy(true);
    try {
      await apiPost(`/api/outreach/recipients/${detail.id}/acknowledge-feedback`);
      await load();
    } catch (err) {
      alert(`Acknowledge failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="mx-auto max-w-4xl px-4 py-6 sm:px-8 sm:py-8 text-sm text-slate-500">Loading…</div>;
  if (!detail)
    return (
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-8 sm:py-8 text-sm text-slate-500">
        Recipient not found.{" "}
        <Link to="/outreach/queue" className="text-brand-700 hover:underline">
          Back to the queue
        </Link>
      </div>
    );

  const name = [detail.firstName, detail.lastName].filter(Boolean).join(" ") || "(no name)";

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-8 sm:py-8">
      <Link to="/outreach/queue" className="text-xs text-slate-500 hover:text-slate-900">
        ← Outreach queue
      </Link>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{name}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {detail.dealerName} · {detail.campaignName} ·{" "}
            {detail.eventDate
              ? `${detail.type === "sales" ? "purchased" : "serviced"} ${new Date(
                  detail.eventDate
                ).toLocaleDateString()}`
              : detail.type}
          </p>
        </div>
        <StatusBadge status={detail.sendStatus} skipReason={detail.skipReason} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contact</h2>
          <dl className="mt-2 space-y-1 text-sm">
            <Row label="Email" value={detail.email ?? "—"} muted={!detail.canEmail} />
            <Row label="Phone" value={detail.phone ?? "—"} muted={!detail.canText} />
            {detail.vehicle && <Row label="Vehicle" value={detail.vehicle} />}
          </dl>
          {(!detail.canEmail || !detail.canText) && (
            <p className="mt-2 text-[11px] text-rose-600">
              Struck-through channels can't be used for this customer — they either
              opted out at the CRM level or have no contact on file for that channel.
            </p>
          )}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Survey answers
          </h2>
          <dl className="mt-2 space-y-1 text-sm">
            <Row label="What brought them in" value={detail.howFoundUs ?? "—"} />
            <Row label="Did we do a great job?" value={greatJobLabel(detail)} />
            <Row
              label="Satisfaction"
              value={detail.satisfactionScore ? `${detail.satisfactionScore}/5` : "—"}
            />
            <Row
              label="Has a Yelp account"
              value={detail.hasYelpAccount == null ? "—" : detail.hasYelpAccount ? "Yes" : "No"}
            />
            <Row label="Sent to" value={detail.reviewDestination ?? "—"} />
            <Row label="Routed to" value={detail.routedTo ?? "—"} />
          </dl>
        </section>
      </div>

      {detail.teamMemberRecognition && (
        <section className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
            Team members they wanted to recognize
          </h2>
          <p className="mt-2 whitespace-pre-wrap text-sm text-emerald-900">
            {detail.teamMemberRecognition}
          </p>
        </section>
      )}

      {detail.privateFeedback && (
        <section className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-rose-700">
              Private feedback to the manager
            </h2>
            {detail.privateFeedbackAcknowledgedAt ? (
              <span className="text-[11px] text-emerald-700">
                Acknowledged {new Date(detail.privateFeedbackAcknowledgedAt).toLocaleString()}
              </span>
            ) : (
              <button
                onClick={() => void acknowledge()}
                disabled={busy}
                className="rounded-md bg-rose-600 px-2 py-1 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {busy ? "…" : "Mark handled"}
              </button>
            )}
          </div>
          <blockquote className="mt-2 whitespace-pre-wrap text-sm text-slate-800">
            {detail.privateFeedback}
          </blockquote>
        </section>
      )}

      {detail.matchedReview && (
        <section className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
            Matched public review — loop closed 🎉
          </h2>
          <p className="mt-2 text-sm text-slate-800">
            {"★".repeat(detail.matchedReview.stars)}
            {"☆".repeat(5 - detail.matchedReview.stars)} by{" "}
            <span className="font-medium">{detail.matchedReview.reviewerName ?? "Anonymous"}</span>{" "}
            on {new Date(detail.matchedReview.postedAt).toLocaleDateString()} (
            {detail.matchedReview.source})
          </p>
          {detail.matchedReview.text && (
            <blockquote className="mt-1 line-clamp-4 text-sm text-slate-600">
              {detail.matchedReview.text}
            </blockquote>
          )}
        </section>
      )}

      <section className="mt-6">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Timeline</h2>
        <ol className="mt-2 space-y-0.5">
          {detail.events.map((e) => (
            <li
              key={e.id}
              className="flex items-baseline justify-between rounded-lg bg-white px-3 py-2 text-sm ring-1 ring-slate-100"
            >
              <span className="text-slate-800">
                {EVENT_LABEL[e.type] ?? e.type}
                {e.channel ? ` (${e.channel})` : ""}
                {e.metadata?.skipReason ? (
                  <span className="text-slate-400">
                    {" — "}
                    {skipReasonLabel(String(e.metadata.skipReason))}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 pl-3 text-xs text-slate-400">
                {new Date(e.createdAt).toLocaleString()}
              </span>
            </li>
          ))}
          {detail.events.length === 0 && (
            <li className="text-sm text-slate-500">No events yet.</li>
          )}
        </ol>
      </section>
    </div>
  );
}

/**
 * Renders the stored satisfaction score back as Don's Yes/No question. Uses the
 * campaign's own threshold rather than assuming the default of 4, so a campaign
 * tuned to a different threshold doesn't get mislabelled here.
 */
function greatJobLabel(detail: OutreachRecipientDetail): string {
  if (detail.satisfactionScore == null) return "—";
  const threshold = detail.campaignSatisfactionThreshold ?? 4;
  return detail.satisfactionScore >= threshold ? "Yes, absolutely!" : "No";
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className={`text-right ${muted ? "text-slate-400 line-through" : "text-slate-900"}`}>
        {value}
      </dd>
    </div>
  );
}
